import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
        setMobileCatOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isTransparent = !scrolled && !mobileOpen;

  return (
    <nav
      ref={mobileRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap transition-colors duration-300 ${
              isTransparent ? 'text-white' : 'text-foreground-950'
            }`}
          >
            Mulebuy Spreadsheet<span className={isTransparent ? 'text-accent-400' : 'text-accent-500'}>.</span>
          </Link>

          <div className="hidden md:flex items-center gap-5 lg:gap-7">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isTransparent ? 'text-white/85 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isTransparent ? 'text-white/85 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg border border-background-200 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/shopping-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isTransparent ? 'text-white/85 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Shopping Guide
            </Link>

            <Link
              to="/mulebuy-spreadsheet-comparison-2026"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              Comparison
            </Link>
            <Link
              to="/article/mulebuy-spreadsheet-safety-tips"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              Safety Guide
            </Link>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Mulebuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30 backdrop-blur-sm'
                  : 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
              }`}
            >
              Buy It Now
            </a>

            <button
              onClick={() => { setMobileOpen(!mobileOpen); setMobileCatOpen(false); }}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isTransparent ? 'text-white hover:bg-white/15' : 'text-foreground-950 hover:bg-background-100'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-6 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
            >
              <i className="ri-home-4-line text-lg"></i>
              Home
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 border-l-2 border-background-200 pl-4 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
                    onClick={() => { setMobileOpen(false); setMobileCatOpen(false); }}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/shopping-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
            >
              <i className="ri-book-read-line text-lg"></i>
              Shopping Guide
            </Link>

            <Link
              to="/mulebuy-spreadsheet-comparison-2026"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
            >
              <i className="ri-bar-chart-box-line text-lg"></i>
              Comparison
            </Link>

            <Link
              to="/article/mulebuy-spreadsheet-safety-tips"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
            >
              <i className="ri-shield-check-line text-lg"></i>
              Safety Guide
            </Link>

            <div className="pt-3 mt-3 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <i className="ri-external-link-line text-lg"></i>
                Mulebuy Spreadsheet Store
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}